import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { focusRing } from '../styles/ui';

// `size="lg"` is for standalone contexts like the product detail page, where
// the stepper is the primary CTA rather than a small control inside a card
// row — it keeps the same premium white/green look but at a size that reads
// as a deliberate ecommerce component (120-160px wide, 42-48px tall) instead
// of a compact in-card control.
const SIZE_STYLES = {
  // Fits a compact card's fixed 74px action slot.
  xs: {
    root: 'gap-0.5 px-0.5 py-0.5',
    button: 'w-6 h-6',
    icon: 12,
    quantity: 'min-w-[1.1rem] text-xs',
  },
  sm: {
    root: 'gap-1 px-1 py-1',
    button: 'w-7 h-7',
    icon: 14,
    quantity: 'min-w-[1.5rem] text-sm',
  },
  lg: {
    root: 'gap-0 px-1.5 py-1.5 w-full max-w-[152px]',
    button: 'w-9 h-9',
    icon: 16,
    quantity: 'flex-1 text-base',
  },
};

const QuantityStepper = ({ quantity, onChange, min = 0, max = 99, size = 'sm' }) => {
  const styles = SIZE_STYLES[size] || SIZE_STYLES.sm;

  return (
    <div
      className={`inline-flex items-center justify-between bg-white border border-emerald-200 rounded-full shadow-soft ${styles.root}`}
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(min, quantity - 1))}
        aria-label="Decrease quantity"
        className={`shrink-0 flex items-center justify-center rounded-full text-emerald-700 border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 active:scale-90 transition-all duration-200 ${focusRing} ${styles.button}`}
      >
        <Minus size={styles.icon} aria-hidden="true" />
      </button>
      <span
        className={`text-center font-bold text-emerald-950 ${styles.quantity}`}
        aria-live="polite"
      >
        {quantity}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, quantity + 1))}
        aria-label="Increase quantity"
        className={`shrink-0 flex items-center justify-center rounded-full text-white bg-gradient-primary hover:shadow-glow-teal active:scale-90 transition-all duration-200 ${focusRing} ${styles.button}`}
      >
        <Plus size={styles.icon} aria-hidden="true" />
      </button>
    </div>
  );
};

export default QuantityStepper;
